import { AccordionLink } from '../../components/AccordionLink';
import { Button } from '../../components/Button';
import { ContentParser } from '../../components/ContentParser';
import { ExpandableText } from '../../components/ExpandableText';
import { DesktopOnly, MobileOnly } from '../../components/Layout';
import { NotificationBox } from '../../components/NotificationBox';
import { NotificationActions } from '../../components/NotificationBox/NotificationBox.styles';
import { FromPriceDisplay } from '../../components/PriceDisplay';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { StickyActionBar } from '../../components/StickyActionBar';
import {
  CategoryLabel,
  DescriptionSection,
  HeaderGroup,
  NotificationSection,
  PromotionBadge,
  ScrollArea,
  Spacer,
  TermsSection,
  Title,
  Wrapper,
} from './ContentPanel.styles';
import type { ContentPanelProps } from './ContentPanel.types';

export function ContentPanel({
  title,
  price,
  discount,
  priceLabel = 'from',
  promotionName,
  categoryLabel,
  description,
  termsContent,
  termsLabel = 'Terms & Conditions',
  onReserve,
  reserveLabel = 'Reserve',
  reserveUnderPrice = false,
  isLoading = false,
  telephoneOnly,
  unavailable,
}: ContentPanelProps) {
  if (isLoading) {
    return (
      <Wrapper>
        <ScrollArea>
          <HeaderGroup>
            <SkeletonBlock variant="heading" />
            <SkeletonBlock width="35%" height="1.25rem" />
          </HeaderGroup>
          <Spacer />
          <CategoryLabel as="div">
            <SkeletonBlock width="25%" height="0.875rem" />
          </CategoryLabel>
          <DescriptionSection>
            <SkeletonBlock variant="text" />
            <SkeletonBlock variant="text" />
            <SkeletonBlock variant="text" width="70%" />
          </DescriptionSection>
          <Spacer />
          <TermsSection>
            <SkeletonBlock height="48px" />
          </TermsSection>
        </ScrollArea>
        <StickyActionBar>
          <SkeletonBlock variant="button" />
        </StickyActionBar>
      </Wrapper>
    );
  }

  const reserveButton =
    !telephoneOnly && !unavailable && onReserve ? (
      <Button variant="primary" fullWidth onClick={onReserve}>
        {reserveLabel}
      </Button>
    ) : null;

  return (
    <Wrapper>
      <ScrollArea>
        <HeaderGroup>
          {promotionName && <PromotionBadge>{promotionName}</PromotionBadge>}
          <Title>{title}</Title>
          {price && (
            <FromPriceDisplay
              amount={price.amount}
              currencyCode={price.currencyCode}
              label={priceLabel}
              discount={discount}
              size="small"
            />
          )}
          {reserveUnderPrice && reserveButton && <DesktopOnly>{reserveButton}</DesktopOnly>}
        </HeaderGroup>
        <Spacer />
        {categoryLabel && <CategoryLabel>{categoryLabel}</CategoryLabel>}
        {description && (
          <DescriptionSection>
            <ExpandableText text={description} />
          </DescriptionSection>
        )}
        <Spacer />
        {termsContent && (
          <TermsSection>
            <AccordionLink label={termsLabel}>{termsContent}</AccordionLink>
          </TermsSection>
        )}
        {telephoneOnly && (
          <NotificationSection>
            <NotificationBox
              title={telephoneOnly.title}
              subtitle={
                telephoneOnly.subtitle ? <ContentParser text={telephoneOnly.subtitle} /> : undefined
              }
            />
          </NotificationSection>
        )}
        {!telephoneOnly && unavailable && (
          <NotificationSection>
            <NotificationBox
              title={unavailable.title}
              subtitle={
                unavailable.subtitle ? <ContentParser text={unavailable.subtitle} /> : undefined
              }
              cta={
                unavailable.ctaLabel || unavailable.secondaryCtaLabel ? (
                  <NotificationActions
                    $stacked={!!unavailable.ctaLabel && !!unavailable.secondaryCtaLabel}
                  >
                    {unavailable.ctaLabel && (
                      <Button
                        variant="primary"
                        size={unavailable.secondaryCtaLabel ? 'large' : undefined}
                        fullWidth={!!unavailable.secondaryCtaLabel}
                        onClick={unavailable.onCtaClick}
                      >
                        {unavailable.ctaLabel}
                      </Button>
                    )}
                    {unavailable.secondaryCtaLabel && (
                      <Button
                        variant="secondary"
                        size="large"
                        fullWidth={!!unavailable.ctaLabel}
                        onClick={unavailable.onSecondaryCtaClick}
                      >
                        {unavailable.secondaryCtaLabel}
                      </Button>
                    )}
                  </NotificationActions>
                ) : undefined
              }
            />
          </NotificationSection>
        )}
      </ScrollArea>
      {reserveButton &&
        (reserveUnderPrice ? (
          <MobileOnly>
            <StickyActionBar>{reserveButton}</StickyActionBar>
          </MobileOnly>
        ) : (
          <StickyActionBar>{reserveButton}</StickyActionBar>
        ))}
    </Wrapper>
  );
}
